// frontend/src/components/RootRoute.js

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
// Landing page — kept EAGER so first paint is not gated on an extra chunk fetch.
import Homepage from "../pages/Homepage";

/**
 * What "/" renders. Sits beside ProtectedRoute (its mirror image: that one
 * guards the app from anonymous visitors, this one keeps signed-in visitors off
 * the marketing page). Backlog #158 — docs/homepage-auth-flash-investigation.md.
 *
 * Two independent signals, deliberately not merged:
 *
 * - `authHint` — the non-httpOnly `lf_auth` cookie, known SYNCHRONOUSLY, so the
 *   redirect happens on the very first render and Homepage never mounts. It is
 *   forgeable and readable by any XSS, so it may only ever mean "try the
 *   dashboard first": we navigate UNCONDITIONALLY to /dashboard and let
 *   ProtectedRoute / computePostLoginPath decide where the visitor really
 *   belongs. Never derive a destination from it.
 * - `isAuthenticated` — the real, server-confirmed session. Covers the visitor
 *   who has a live session but no hint yet (sessions predating the backend's
 *   self-heal). Preserves the pre-#158 behaviour that Homepage used to own.
 *
 * INVARIANT: `loading` must NEVER gate <Homepage/>. A hint-less visitor is
 * treated as anonymous and gets the marketing page immediately — blanking the
 * lead-generation homepage for a full auth round trip is worse than the flash
 * this whole change exists to remove.
 */
const RootRoute = () => {
  const { authHint, isAuthenticated, loading } = useAuth();

  if (authHint || (!loading && isAuthenticated)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Homepage />;
};

export default RootRoute;
